/* Both elements track `--hover-x`, the raw pointer pixel in `.canvas`'s own coordinate space
   (sidebar width already folded in by `hover-guide.tsx`) — never a day-column-snapped position,
   so the line and pill move continuously under the cursor the way the old guide did.

   Neither lives inside `.row` (its own stacking context, via `transform`) or inside `.canvas`'s
   day-grid layer (`z-index: 0`, home to `.todayLine`'s `-1`): both are `.canvas` siblings of
   `.headerGroup` and the row group, so their own z-index competes directly in `.canvas`'s
   stacking context instead of being sealed inside a row's. See `gantt-chart.module.scss` for the
   traps this avoids. */

.line {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--hover-x);
  width: 0.2rem;
  transform: translateX(-50%);
  background-color: var(--color-grayscale-light-slate);
  pointer-events: none;
  /* Above every row (bars at 4, resize strips / off-screen chips at 5) and below `.headerGroup`
     (11) — the OPPOSITE side of the row stack from `.todayLine`'s `-1`, per the brief. Sitting
     below the sticky, opaque header means the header paints over this line wherever it travels
     behind it, which is what makes the line read as starting at the top of the body rather than
     running through the header too — matching the old guide, which lived only inside the chart
     body (`gantt-chart/components/chart.tsx`) and never inside `chart-wrapper.tsx`'s
     `HeaderContainer`.

     REVIEW M7 — THE `pointer-events` DECISION ON THE TIMELINE HEADER, RECORDED. The Task 21 plan
     asked for this to be revisited and nothing in the diff answered it, so: `.headerGroup` keeps
     its default `pointer-events: auto` and NOTHING here changes that. The guide still tracks a
     pointer moving over the header — `hover-guide.tsx` listens on `.canvas`, and the header is a
     descendant, so the `mousemove` bubbles — but the header is opaque and sits at `z-index: 11`
     above this line's 6, so the line is simply hidden for the part of its travel behind the
     header while the PILL (`z-index: var(--layer-tooltip)`) stays visible above it. That is the
     old chart's behaviour, and it is the one we want: the pill is the thing a user reads while
     scrubbing the date row. Setting `pointer-events: none` on the header would additionally break
     every control inside it. Decision: leave it. Closed, not deferred. */
  z-index: 6;
}

/* Zero-height so it adds no layout space of its own — the overlay `--gantt-header-height`'s
   comment in `gantt-chart.module.scss` asks for, not a taller header. `position: sticky; top: 0`
   on THIS box (not `.pill`) is what keeps the pill visible at a constant height while `.scroller`
   scrolls vertically.

   STICKY HAS TWO PRECONDITIONS HERE AND BOTH HAVE TO HOLD.

   (1) CONTAINING BLOCK, which a sticky box is clamped to. This one's is `.canvas`, the whole
   scrollable content, so the permitted offset range is the full scroll height — the same reason
   `.headerGroup` puts `position: sticky` on itself rather than on `.header` inside it.

   (2) FLOW POSITION, which the original version of this comment reasoned past. A `top` inset only
   ever pushes a box DOWN from where normal flow already put it; sticky can never LIFT a box to the
   top of the scrollport. So this rule is inert unless `gantt-chart.tsx` renders `HoverGuide` at
   the TOP of `.canvas`'s child list, and it is inert in a way that looks like nothing at all:
   `.line` is `position: absolute` and keeps working, so the failure reads as "the guide has no
   date" rather than as a layout bug. It shipped rendered LAST, after the row group, which put the
   pill's flow position below every row — measured at y=1888 in a 600px scrollport with 1760px of
   rows, invisible at every scroll position. `gantt-chart.tsx` now renders it first and says why
   there. (1) was correct all along and is not sufficient on its own. */
.pillLayer {
  position: sticky;
  top: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
  /* Above `.headerGroup` (11) — the pill is meant to hang OVER the header, the opposite of
     `.line` above, which the header is meant to hide. Old parity: `chart-wrapper.tsx:169-184`'s
     `DateTrackerLabel`, `z-index: var(--layer-tooltip)`. */
  z-index: var(--layer-tooltip);
}

.pill {
  position: absolute;
  /* Old `DateTrackerLabel` sat at `bottom: 0` inside a sticky header box that was 9.4rem tall,
     whose opaque part stopped at 6.4rem (now `--gantt-header-height`) — so its actual rendered
     top landed right where the opaque header ends, ~3rem above that `bottom: 0`. Anchoring this
     pill's TOP at `--gantt-header-height` instead reproduces the same visible position without
     resurrecting the 3rem of dead, transparent header space Phase 1 removed. */
  top: var(--gantt-header-height);
  left: var(--hover-x);
  transform: translateX(-50%);
  background-color: black;
  color: var(--color-grayscale-white);
  text-align: center;
  padding: 0.8rem 1rem;
  border-radius: 0.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  white-space: nowrap;
}
